import {
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  Menu,
  MenuButton,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { AppointmentStatus } from '@shared/types';
import { FiActivity, FiChevronDown, FiClock } from 'react-icons/fi';

import { computeEndSessionReason } from './endSessionReason';
import StatusConfirmationModal from './StatusConfirmationModal';
import UpdateDurationModalV2 from './UpdateDurationModalV2';

export interface EndCallModalV2Props {
  isOpen: boolean;
  onClose: () => void;

  // Status
  selectedStatus: AppointmentStatus | null;
  onStatusChange: (status: AppointmentStatus) => void;
  currentStatus?: AppointmentStatus | null;

  // Duration
  calculatedDuration: number;
  editedDuration: number | null;
  onDurationEdit: (minutes: number, reason: string) => void;

  // Phone appointment
  apptOccurredOnThePhone: boolean;
  onPhoneAppointmentChange: (checked: boolean) => void;

  // Actions
  onConfirm: () => Promise<void>;
  isPending: boolean;
  canConfirm: boolean;
}

/**
 * V2 of EndCallModal that works with hook-based state management.
 *
 * Differences from V1:
 * - No HealthieContext dependency
 * - All state passed as props from useEndAppointment hook
 * - No setState pattern - uses callbacks
 * - Simplified for provider-only flow
 *
 * Features:
 * - Status selection (Occurred, No-Show, Re-Scheduled, Cancelled)
 * - Duration display with edit functionality
 * - UpdateDurationModalV2 integration
 * - Loading states during API calls
 */
export const EndCallModalV2: React.FC<EndCallModalV2Props> = ({
  isOpen,
  onClose,
  selectedStatus,
  onStatusChange,
  currentStatus,
  calculatedDuration,
  editedDuration,
  onDurationEdit,
  apptOccurredOnThePhone,
  onPhoneAppointmentChange,
  onConfirm,
  isPending,
  canConfirm,
}) => {
  const {
    isOpen: isUpdateModalOpen,
    onOpen: onUpdateModalOpen,
    onClose: onUpdateModalClose,
  } = useDisclosure();

  const {
    isOpen: isStatusConfirmModalOpen,
    onOpen: onStatusConfirmModalOpen,
    onClose: onStatusConfirmModalClose,
  } = useDisclosure();

  const displayDuration = editedDuration ?? calculatedDuration;

  const handleStatusChange = (newStatus: AppointmentStatus) => {
    onStatusChange(newStatus);
  };

  const confirmationReason = computeEndSessionReason({
    selectedStatus,
    currentStatus,
    calculatedDuration,
  });

  const handleEndSession = () => {
    if (confirmationReason !== null) {
      onStatusConfirmModalOpen();
      return;
    }

    onConfirm();
  };

  return (
    <>
      <Modal isOpen={isOpen} size="sm" onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent
          bg="surface.default"
          overflow="hidden"
          borderColor="border.default"
          borderWidth={1}
          borderRadius={10}
          color="text.default"
        >
          <ModalHeader
            as={Heading}
            fontFamily="Plus Jakarta Sans"
            size="md"
            mt={5}
            pb={0}
          >
            End Session
          </ModalHeader>

          <ModalBody as={VStack} gap={0} position="relative" mb={3}>
            <Text color="text.light">
              The session will end for everyone and the appointment will be
              updated with the information below.
            </Text>

            {/* Status Selection */}
            <FormControl mt={5}>
              <HStack as={FormLabel} size="sm">
                <FiActivity />
                <span>Assign Status</span>
              </HStack>
              <Menu matchWidth placement="bottom" flip={false}>
                {({ isOpen }) => (
                  <>
                    <MenuButton
                      as={Button}
                      width="100%"
                      justifyContent="space-between"
                      colorScheme="green"
                      variant="outline"
                      rightIcon={
                        <FiChevronDown
                          style={{
                            transform: isOpen
                              ? 'rotate(180deg)'
                              : 'rotate(0deg)',
                            transition: 'transform 0.1s ease-in-out',
                          }}
                        />
                      }
                      fontWeight="normal"
                      textAlign="left"
                      border="1px solid"
                      borderColor="border.default"
                      data-testid="status-select"
                      _hover={{
                        bg: 'surface.light',
                        borderColor: 'border.default',
                      }}
                      _active={{
                        bg: 'surface.light',
                      }}
                    >
                      {selectedStatus || 'Select status for the appointment'}
                    </MenuButton>
                    <MenuList>
                      <MenuOptionGroup
                        type="radio"
                        value={selectedStatus || undefined}
                        onChange={(value) => {
                          if (typeof value === 'string') {
                            handleStatusChange(value as AppointmentStatus);
                          }
                        }}
                      >
                        {Object.values(AppointmentStatus).map((value) => (
                          <MenuItemOption
                            data-testid={`status-option-${value}`}
                            key={value}
                            value={value}
                            color="text.default"
                            opacity={0.8}
                            _hover={{
                              bg: 'surface.dark',
                              color: 'text.default',
                              opacity: 1,
                            }}
                          >
                            {value}
                          </MenuItemOption>
                        ))}
                      </MenuOptionGroup>
                    </MenuList>
                  </>
                )}
              </Menu>
            </FormControl>

            {/* Duration Display with Edit */}
            <HStack as={FormControl} mt={5}>
              <VStack alignItems="flex-start" flexGrow={1} gap={0}>
                <HStack as={FormLabel} size="sm">
                  <FiClock />
                  <span>Duration (minutes)</span>
                </HStack>
                <Button
                  variant="link"
                  color="text.muted"
                  fontSize="sm"
                  onClick={onUpdateModalOpen}
                  p={0}
                  m={0}
                  minH="auto"
                  height="auto"
                  textDecoration="underline"
                  _hover={{ color: 'text.default' }}
                  data-testid="update-duration-link"
                >
                  Update
                </Button>
              </VStack>
              <Input
                readOnly
                disabled
                isRequired
                flexBasis="30%"
                value={displayDuration}
                type="number"
                data-testid="duration-display"
                _disabled={{
                  color: 'text.default',
                  opacity: 1,
                }}
              />
            </HStack>

            {/* Phone Appointment Checkbox */}
            <FormControl mt={5}>
              <Checkbox
                isChecked={apptOccurredOnThePhone}
                onChange={(e) => onPhoneAppointmentChange(e.target.checked)}
                colorScheme="orange"
                data-testid="phone-appointment-checkbox"
              >
                <VStack alignItems="flex-start" gap={0.5}>
                  <Text color="text.default" fontSize="sm">
                    Appointment occurred on the phone.
                  </Text>
                  <Text color="text.muted" fontSize="xs">
                    Use this only this if the appointment was conducted outside
                    this app. Don&apos;t use it if you used the dial-out
                    feature. It will disable auto-SOAP notes.{' '}
                  </Text>
                </VStack>
              </Checkbox>
            </FormControl>
          </ModalBody>

          <ModalFooter justifyContent="center" mb={5} gap={10}>
            <Button
              data-testid="cancel-end-session-button"
              variant="outline"
              color="text.default"
              borderColor="border.default"
              onClick={onClose}
              _hover={{
                color: 'green.600',
                bgColor: 'surface.light',
                borderColor: 'green.600',
              }}
            >
              Cancel
            </Button>
            <Button
              data-testid="confirm-end-session-button"
              variant="solid"
              color="white"
              bg="orange.500"
              isDisabled={!canConfirm}
              onClick={handleEndSession}
              isLoading={isPending}
              _hover={{
                bg: 'orange.600',
              }}
            >
              End Session
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Update Duration Modal */}
      <UpdateDurationModalV2
        isOpen={isUpdateModalOpen}
        onClose={onUpdateModalClose}
        calculatedDuration={calculatedDuration}
        editedDuration={editedDuration}
        onConfirm={(minutes, reason) => {
          onDurationEdit(minutes, reason);
          onUpdateModalClose();
        }}
      />

      {/* Status Confirmation Modal */}
      {selectedStatus && (
        <StatusConfirmationModal
          isOpen={isStatusConfirmModalOpen}
          onClose={onStatusConfirmModalClose}
          status={selectedStatus}
          duration={calculatedDuration}
          onConfirm={onConfirm}
          previousStatus={currentStatus ?? null}
        />
      )}
    </>
  );
};
